/**
 * A2UI Select Component
 * Dropdown select with options and data binding
 */

import React, { useState, useEffect } from 'react';
import { Form } from 'react-bootstrap';

const A2UISelect = ({ 
  label,
  placeholder,
  value,
  dataBinding,
  options = [],
  optionsBinding,
  multiple = false,
  required = false,
  disabled = false,
  helperText,
  errorText,
  action,
  style,
  resolveValue,
  onDataChange,
  onAction,
  surfaceId,
  componentId
}) => {
  const resolvedLabel = resolveValue ? resolveValue(label, surfaceId) : label?.literalString || '';
  const resolvedPlaceholder = resolveValue ? resolveValue(placeholder, surfaceId) : placeholder?.literalString || 'Select...';
  const resolvedValue = resolveValue ? resolveValue(value || dataBinding, surfaceId) : '';
  const resolvedDisabled = resolveValue ? resolveValue(disabled, surfaceId) : disabled?.literalBoolean || false;
  const resolvedHelper = resolveValue ? resolveValue(helperText, surfaceId) : helperText?.literalString || '';
  const resolvedError = resolveValue ? resolveValue(errorText, surfaceId) : errorText?.literalString || '';
  
  // Resolve options - can be literal array or data-bound
  let resolvedOptions = options;
  if (optionsBinding?.path && resolveValue) {
    resolvedOptions = resolveValue(optionsBinding, surfaceId) || [];
  }
  
  const [localValue, setLocalValue] = useState(resolvedValue || '');

  useEffect(() => {
    setLocalValue(resolvedValue || '');
  }, [resolvedValue]);

  const handleChange = (e) => {
    const newValue = multiple 
      ? Array.from(e.target.selectedOptions, option => option.value)
      : e.target.value;
    
    setLocalValue(newValue);
    
    if (dataBinding?.path && onDataChange) {
      onDataChange(surfaceId, dataBinding.path, newValue);
    }

    // Trigger action on change if configured
    if (action && onAction) {
      onAction({
        name: action.name,
        surfaceId,
        sourceComponentId: componentId,
        context: action.context,
        timestamp: new Date().toISOString()
      });
    }
  };

  return (
    <Form.Group className="a2ui-select mb-3" style={style}>
      {resolvedLabel && (
        <Form.Label>{resolvedLabel}</Form.Label>
      )}
      <Form.Select
        value={localValue}
        onChange={handleChange}
        disabled={resolvedDisabled}
        required={required}
        multiple={multiple}
        isInvalid={!!resolvedError}
      >
        {!multiple && (
          <option value="">{resolvedPlaceholder}</option>
        )}
        {resolvedOptions.map((option, index) => {
          const optionValue = typeof option === 'object' ? option.value : option;
          const optionLabel = typeof option === 'object' ? option.label : option;
          return (
            <option key={index} value={optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </Form.Select>
      {resolvedError && (
        <Form.Control.Feedback type="invalid" style={{ display: 'block' }}>
          {resolvedError}
        </Form.Control.Feedback>
      )}
      {resolvedHelper && !resolvedError && (
        <Form.Text className="text-muted">
          {resolvedHelper}
        </Form.Text>
      )}
    </Form.Group>
  );
};

export default A2UISelect;
